import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";

const DEMOS = [
  { label: "Demo Homeowner", email: "homeowner@demo.com" },
  { label: "Demo Veteran", email: "veteran@demo.com" },
  { label: "Demo Dispatcher", email: "admin@valorclean.com", pw: "admin123" },
];

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", is_veteran: false });
  const [loading, setLoading] = useState(false);

  const redirectTo = location.state?.from || null;

  const go = (user) => {
    if (user.role === "admin") navigate("/admin");
    else navigate(redirectTo || "/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = isLogin
        ? await login(form.email, form.password)
        : await register(form);
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      go(user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (d) => {
    setLoading(true);
    try {
      const user = await login(d.email, d.pw || "demo123");
      toast.success("Signed in");
      go(user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* left brand */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 grain relative">
        <Link to="/" className="w-fit">
          <span className="inline-flex bg-white rounded-xl p-2">
            <img src="/logo.jpg" alt="Solar Stars LLC" className="h-14 w-auto object-contain" />
          </span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-black leading-tight">Precision cleaning for peak solar output.</h2>
          <p className="text-slate-300 mt-4 max-w-md">Manage every booking, reschedule in a tap, and keep your panels performing — all from one dashboard.</p>
          <div className="inline-flex items-center gap-2 mt-8 bg-white/10 px-4 py-2 rounded-full text-sm font-semibold">
            <ShieldCheck className="w-4 h-4 text-amber-400" /> Veteran-Owned & Operated
          </div>
        </div>
        <div className="text-sm text-slate-500">© {new Date().getFullYear()} Stars Cleaning Solar Care</div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-8" data-testid="auth-back-home"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
          <h1 className="font-heading text-3xl font-black text-slate-900">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-slate-500 mt-2">{isLogin ? "Sign in to manage your solar cleanings." : "Book and manage cleanings in minutes."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="auth-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" placeholder="Jordan Rivera" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="auth-password-input" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11" placeholder="••••••••" />
            </div>
            {!isLogin && (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className="w-5 h-5 text-emerald-700" />
                  <div><div className="text-sm font-bold text-slate-900">I'm a veteran / first responder</div><div className="text-xs text-slate-500">Unlocks 15% off your first cleaning</div></div>
                </div>
                <Switch checked={form.is_veteran} onCheckedChange={(v) => setForm({ ...form, is_veteran: v })} data-testid="auth-veteran-toggle" />
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full h-11 rounded-full bg-emerald-700 hover:bg-emerald-800 font-bold gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}{isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <div className="text-center text-sm text-slate-500 mt-5">
            {isLogin ? (
              <>New here? <Link to="/register" className="text-emerald-700 font-semibold" data-testid="auth-switch-register">Create an account</Link></>
            ) : (
              <>Already have an account? <Link to="/login" className="text-emerald-700 font-semibold" data-testid="auth-switch-login">Sign in</Link></>
            )}
          </div>

          <div className="relative my-6"><div className="border-t border-slate-200" /><span className="absolute inset-0 -top-3 flex justify-center"><span className="bg-white px-3 text-xs text-slate-400 uppercase tracking-wider">Quick demo</span></span></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {DEMOS.map((d) => (
              <Button key={d.email} variant="outline" size="sm" onClick={() => quickLogin(d)} disabled={loading}
                data-testid={`demo-login-${d.email.split("@")[0]}`} className="text-xs">
                {d.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
